// The vocabulary the classifier picks from: approve what the model proposed,
// add your own, retire what is noise. Plus the analysis settings.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createTaxonomy, deleteTaxonomy, getAsoSettings, listTaxonomy, putAsoSettings, updateTaxonomy,
  type TaxonomyEntry, type TaxonomyKind,
} from '@/api/aso';
import { Button } from '@/components/Button';
import { useCanEdit } from '@/components/EditOnly';
import { useToastStore } from '@/store/toast';

const KINDS: { value: TaxonomyKind; label: string; hint: string }[] = [
  { value: 'hook', label: 'Hooks', hint: 'what a picture promises the player' },
  { value: 'tag', label: 'Visual tags', hint: 'how a picture looks' },
  { value: 'cluster', label: 'Text clusters', hint: 'what a phrase is about' },
];

export function TaxonomyTab() {
  const canEdit = useCanEdit();
  const qc = useQueryClient();
  const toast = useToastStore((s) => s.push);
  const [kind, setKind] = useState<TaxonomyKind>('hook');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const q = useQuery({ queryKey: ['aso-taxonomy'], queryFn: listTaxonomy });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['aso-taxonomy'] });

  const add = useMutation({
    mutationFn: () => createTaxonomy({ kind, label: label.trim(), description: description.trim() }),
    onSuccess: () => { toast('Added'); setLabel(''); setDescription(''); invalidate(); },
    onError: (e: any) => toast(e?.message ?? 'Failed to add'),
  });
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: TaxonomyEntry['status'] }) => updateTaxonomy(id, { status }),
    onSuccess: invalidate,
    onError: (e: any) => toast(e?.message ?? 'Failed to update'),
  });
  const del = useMutation({
    mutationFn: deleteTaxonomy,
    onSuccess: invalidate,
    onError: (e: any) => toast(e?.message ?? 'Failed to delete'),
  });

  const rows = (q.data ?? []).filter((t) => t.kind === kind);
  const pending = rows.filter((t) => t.status === 'pending');
  const active = rows.filter((t) => t.status === 'active');
  const rejected = rows.filter((t) => t.status === 'rejected');

  const Row = ({ t }: { t: TaxonomyEntry }) => (
    <tr className="border-t border-border">
      <td className="p-2 align-middle">
        <div className="text-sm text-content">{t.label}</div>
        <div className="text-[11px] text-content-subtle font-mono">{t.slug}{t.created_by === 'ai' ? ' · proposed by the model' : t.created_by === 'seed' ? '' : ' · added by hand'}</div>
      </td>
      <td className="p-2 align-middle text-xs text-content-muted">{t.description ?? ''}</td>
      <td className="p-2 align-middle text-right whitespace-nowrap">
        {canEdit && t.status !== 'active' && <button className="text-xs text-success hover:underline mr-3" onClick={() => setStatus.mutate({ id: t.id, status: 'active' })}>Approve</button>}
        {canEdit && t.status !== 'rejected' && <button className="text-xs text-content-muted hover:underline mr-3" onClick={() => setStatus.mutate({ id: t.id, status: 'rejected' })}>Reject</button>}
        {canEdit && t.created_by !== 'seed' && <button className="text-xs text-danger hover:underline" onClick={() => { if (confirm(`Delete “${t.label}”?`)) del.mutate(t.id); }}>Delete</button>}
      </td>
    </tr>
  );

  const Section = ({ title, items }: { title: string; items: TaxonomyEntry[] }) => items.length === 0 ? null : (
    <div>
      <div className="label mb-1">{title} ({items.length})</div>
      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        <table className="w-full"><tbody>{items.map((t) => <Row key={t.id} t={t} />)}</tbody></table>
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-6">
      <div className="space-y-4">
        <div className="flex gap-1">
          {KINDS.map((k) => (
            <button key={k.value} onClick={() => setKind(k.value)} title={k.hint}
              className={`px-3 py-1.5 rounded-md text-sm ${kind === k.value ? 'bg-primary text-white' : 'bg-surface-muted text-content-muted hover:text-content'}`}>
              {k.label}
            </button>
          ))}
        </div>
        <Section title="Proposed by the model — awaiting approval" items={pending} />
        <Section title="Active" items={active} />
        <Section title="Rejected" items={rejected} />
        {canEdit && (
          <div className="bg-surface border border-border rounded-lg p-3 space-y-2">
            <div className="label">Add a {kind}</div>
            <input className="input" placeholder="Label, e.g. Nostalgia" value={label} onChange={(e) => setLabel(e.target.value)} />
            <input className="input" placeholder="When it applies (shown to the model)" value={description} onChange={(e) => setDescription(e.target.value)} />
            <Button size="sm" disabled={!label.trim() || add.isPending} onClick={() => add.mutate()}>Add</Button>
          </div>
        )}
      </div>
      <SettingsPanel />
    </div>
  );
}

function SettingsPanel() {
  const canEdit = useCanEdit();
  const qc = useQueryClient();
  const toast = useToastStore((s) => s.push);
  const q = useQuery({ queryKey: ['aso-settings'], queryFn: getAsoSettings });
  type Form = { vision_model: string; text_model: string; vision_budget: string; auto_analyze: boolean; keyword_provider: string; keyword_budget: string; asomobile_token: string };
  const [form, setForm] = useState<Form | null>(null);
  const current: Form | null = form ?? (q.data ? {
    vision_model: q.data.vision_model, text_model: q.data.text_model, vision_budget: String(q.data.vision_budget), auto_analyze: q.data.auto_analyze,
    keyword_provider: q.data.keyword_provider, keyword_budget: String(q.data.keyword_budget), asomobile_token: '',
  } : null);
  const save = useMutation({
    mutationFn: () => {
      const { asomobile_token, ...rest } = current!;
      return putAsoSettings({ ...rest, vision_budget: Number(rest.vision_budget), keyword_budget: Number(rest.keyword_budget), ...(asomobile_token ? { asomobile_token } : {}) });
    },
    onSuccess: () => { toast('Settings saved'); setForm(null); qc.invalidateQueries({ queryKey: ['aso-settings'] }); },
    onError: (e: any) => toast(e?.message ?? 'Failed to save'),
  });
  const clearToken = useMutation({
    mutationFn: () => putAsoSettings({ asomobile_token: '' }),
    onSuccess: () => { toast('Token removed'); qc.invalidateQueries({ queryKey: ['aso-settings'] }); },
    onError: (e: any) => toast(e?.message ?? 'Failed'),
  });
  if (!current) return null;
  const set = (patch: Partial<typeof current>) => setForm({ ...current, ...patch });
  return (
    <div className="bg-surface border border-border rounded-lg p-4 space-y-3 h-fit">
      <div className="font-medium text-content">Analysis settings</div>
      <div>
        <label className="label">Vision model</label>
        <input className="input mt-1" value={current.vision_model} disabled={!canEdit} onChange={(e) => set({ vision_model: e.target.value })} placeholder={q.data?.defaults?.vision_model} />
        <p className="text-[11px] text-content-subtle mt-1">Classifies icons and screenshots. Uses the OpenAI key from Settings.</p>
      </div>
      <div>
        <label className="label">Text model</label>
        <input className="input mt-1" value={current.text_model} disabled={!canEdit} onChange={(e) => set({ text_model: e.target.value })} placeholder={q.data?.defaults?.text_model} />
        <p className="text-[11px] text-content-subtle mt-1">Groups phrases into clusters, one call per snapshot.</p>
      </div>
      <div>
        <label className="label">Vision budget per snapshot</label>
        <input className="input mt-1" type="number" min={0} value={current.vision_budget} disabled={!canEdit} onChange={(e) => set({ vision_budget: e.target.value })} />
        <p className="text-[11px] text-content-subtle mt-1">Max pictures sent to the model per run. Pictures seen before are never re-sent.</p>
      </div>
      <label className="flex items-center gap-2 text-sm text-content">
        <input type="checkbox" checked={current.auto_analyze} disabled={!canEdit} onChange={(e) => set({ auto_analyze: e.target.checked })} />
        Analyse every snapshot automatically
      </label>
      <div className="border-t border-border pt-3 font-medium text-content">Keyword metrics</div>
      <div>
        <label className="label">Provider</label>
        <select className="input mt-1" value={current.keyword_provider} disabled={!canEdit} onChange={(e) => set({ keyword_provider: e.target.value })}>
          {(q.data?.keyword_providers ?? ['asomobile']).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      <div>
        <label className="label">ASOMobile API token</label>
        <input className="input mt-1 font-mono" type="password" value={current.asomobile_token} disabled={!canEdit} onChange={(e) => set({ asomobile_token: e.target.value })}
          placeholder={q.data?.asomobile_token_set ? `Set: ${q.data.asomobile_token_masked} — paste to replace` : 'Paste the token from app.asomobile.net → API'} />
        {q.data?.asomobile_token_set && canEdit && <button className="text-[11px] text-danger hover:underline mt-1" onClick={() => { if (confirm('Remove the ASOMobile token?')) clearToken.mutate(); }}>Remove token</button>}
      </div>
      <div>
        <label className="label">Apps per snapshot</label>
        <input className="input mt-1" type="number" min={0} value={current.keyword_budget} disabled={!canEdit} onChange={(e) => set({ keyword_budget: e.target.value })} />
        <p className="text-[11px] text-content-subtle mt-1">Each app is one provider request. Scopes opt in to keyword collection individually.</p>
      </div>
      {canEdit && <Button size="sm" disabled={!form || save.isPending} onClick={() => save.mutate()}>Save</Button>}
    </div>
  );
}
